import { Controller } from "stimulus"
import $ from "jquery"

const LOG_PREFIX = "[accounts/payment]"

function payLog(message, detail) {
  if (detail !== undefined) {
    console.log(LOG_PREFIX, message, detail)
  } else {
    console.log(LOG_PREFIX, message)
  }
}

function payWarn(message, detail) {
  if (detail !== undefined) {
    console.warn(LOG_PREFIX, message, detail)
  } else {
    console.warn(LOG_PREFIX, message)
  }
}

function payError(message, detail) {
  if (detail !== undefined) {
    console.error(LOG_PREFIX, message, detail)
  } else {
    console.error(LOG_PREFIX, message)
  }
}

function summarizePublishableKey(pk) {
  if (!pk) return "(missing)"
  const tail = pk.length > 8 ? pk.slice(-6) : pk
  return `${pk.slice(0, 7)}…${tail} (len=${pk.length})`
}

function loadStripeJs() {
  if (typeof window.Stripe === "function") {
    payLog("Stripe.js: already available (window.Stripe)")
    return Promise.resolve()
  }
  const existing = document.querySelector('script[src^="https://js.stripe.com/v3"]')
  if (existing) {
    payLog("Stripe.js: waiting for existing script tag", existing.src)
    return new Promise((resolve, reject) => {
      const deadline = Date.now() + 15000
      const tick = () => {
        if (typeof window.Stripe === "function") {
          payLog("Stripe.js: became available after poll")
          resolve()
          return
        }
        if (Date.now() > deadline) {
          reject(new Error("Stripe.js did not load. Try a full page refresh."))
          return
        }
        setTimeout(tick, 30)
      }
      tick()
    })
  }
  payLog("Stripe.js: injecting script tag")
  return new Promise((resolve, reject) => {
    const s = document.createElement("script")
    s.src = "https://js.stripe.com/v3/"
    s.async = true
    s.onload = () => {
      if (typeof window.Stripe === "function") {
        payLog("Stripe.js: injected script loaded")
        resolve()
      } else {
        reject(new Error("Stripe.js loaded but is unavailable"))
      }
    }
    s.onerror = () => reject(new Error("Could not load Stripe.js"))
    document.head.appendChild(s)
  })
}

function waitForModalLayout() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve))
  })
}

export default class extends Controller {
  static targets = [
    "stripePaymentForm",
    "paymentElementMount",
    "paymentElementPreload",
    "stripeSubmit",
    "stripeErrorElement",
    "stripeSubmitText",
    "stripeSpinner",
  ]

  connect() {
    payLog("controller connected", {
      classList: this.element.className,
    })

    this._prefetchedRail = null
    this._prefetchMount = false
    this._initPromise = null
    this._piCache = {}

    this._onStripeRailSelected = (ev) => {
      this.prefetchStripeForRail(ev.detail && ev.detail.rail)
    }
    document.addEventListener("accounts:stripe-payment-rail", this._onStripeRailSelected)

    // Bootstrap 4 triggers shown.bs.modal / hidden.bs.modal via jQuery only.
    // Stimulus data-action uses addEventListener, so those handlers never run — bind with jQuery.
    this._onModalShownJq = () => {
      payLog("shown.bs.modal (jQuery) — starting checkout")
      this.modalShown()
    }
    this._onModalHiddenJq = () => {
      payLog("hidden.bs.modal (jQuery)")
      this.modalHidden()
    }
    $(this.element).on("shown.bs.modal", this._onModalShownJq)
    $(this.element).on("hidden.bs.modal", this._onModalHiddenJq)
  }

  disconnect() {
    document.removeEventListener("accounts:stripe-payment-rail", this._onStripeRailSelected)
    if (this._onModalShownJq && this._onModalHiddenJq) {
      $(this.element).off("shown.bs.modal", this._onModalShownJq)
      $(this.element).off("hidden.bs.modal", this._onModalHiddenJq)
    }
    this.teardownStripe()
    this._piCache = {}
  }

  async modalShown() {
    payLog("modalShown: start", { element: this.element })
    const rail = this.selectedPaymentRail()
    payLog("modalShown: payment rail", rail)

    if (this._initPromise) {
      try {
        await this._initPromise
      } catch (_e) {
        /* prefetchStripeForRail logs */
      }
    }

    if (
      this._prefetchedRail === rail &&
      this.paymentElement &&
      this.hasPaymentElementPreloadTarget &&
      this._prefetchMount
    ) {
      payLog("modalShown: moving Payment Element from preload → modal")
      try {
        this.paymentElement.unmount()
      } catch (_e) {
        /* ignore */
      }
      await waitForModalLayout()
      this.paymentElement.mount(this.paymentElementMountTarget)
      this._prefetchMount = false
      this.ensureSubmitHandler()
      this.stripeSubmitTarget.disabled = false
      payLog("modalShown: remount complete")
      return
    }

    if (this._prefetchedRail === rail && this.paymentElement && !this._prefetchMount) {
      payLog("modalShown: Payment Element already in modal")
      await waitForModalLayout()
      this.ensureSubmitHandler()
      return
    }

    this.teardownStripe()
    await this.runCheckoutSetup(rail, this.paymentElementMountTarget, true)
    this._prefetchedRail = rail
    payLog("modalShown: runCheckoutSetup finished")
  }

  modalHidden() {
    // Keep Stripe instances mounted so reopening the modal stays fast (element stays in .payment-element-mount).
  }

  selectedPaymentRail() {
    const el = document.querySelector('input[name="sign_up[payment_method]"]:checked')
    return el && el.getAttribute("data-payment-method") === "ach"
      ? "us_bank_account"
      : "card"
  }

  teardownStripe() {
    this._prefetchedRail = null
    this._prefetchMount = false
    this._initPromise = null

    if (this.paymentElement) {
      try {
        this.paymentElement.unmount()
        payLog("teardownStripe: unmounted Payment Element")
      } catch (_e) {
        payWarn("teardownStripe: unmount threw (ignored)", _e)
      }
      this.paymentElement = null
    }
    this.elements = null
    this.stripe = null
    this._clientSecret = null
    if (this._submitHandler && this.hasStripePaymentFormTarget) {
      this.stripePaymentFormTarget.removeEventListener("submit", this._submitHandler)
      this._submitHandler = null
    }
  }

  async prefetchStripeForRail(rail) {
    if (!rail) {
      payLog("prefetchStripeForRail: clearing (offline or unknown)")
      this.teardownStripe()
      return
    }
    if (!this.hasPaymentElementPreloadTarget) {
      payLog("prefetchStripeForRail: no preload target — skip early init")
      return
    }
    if (this._prefetchedRail === rail && this.paymentElement) {
      payLog("prefetchStripeForRail: already initialized for rail", rail)
      return
    }

    this.teardownStripe()
    payLog("prefetchStripeForRail: starting for rail", rail)

    this._initPromise = this.runCheckoutSetup(rail, this.paymentElementPreloadTarget, false)
      .then(() => {
        this._prefetchedRail = rail
        this._prefetchMount = true
        this._initPromise = null
        payLog("prefetchStripeForRail: complete")
      })
      .catch((e) => {
        this._initPromise = null
        payError("prefetchStripeForRail failed", e)
        this.teardownStripe()
      })

    await this._initPromise
  }

  ensureSubmitHandler() {
    if (!this.hasStripePaymentFormTarget) {
      return
    }
    if (this._submitHandler) {
      this.stripePaymentFormTarget.removeEventListener("submit", this._submitHandler)
    }
    this._submitHandler = (e) => {
      e.preventDefault()
      this.confirmWithStripe()
    }
    this.stripePaymentFormTarget.addEventListener("submit", this._submitHandler)
  }

  /**
   * @param {string} rail — 'card' | 'us_bank_account'
   * @param {HTMLElement} mountEl
   * @param {boolean} attachSubmitHandler
   */
  async runCheckoutSetup(rail, mountEl, attachSubmitHandler) {
    const targetsOk = {
      stripePaymentForm: this.hasStripePaymentFormTarget,
      paymentElementMount: this.hasPaymentElementMountTarget,
      stripeSubmit: this.hasStripeSubmitTarget,
      stripeErrorElement: this.hasStripeErrorElementTarget,
      stripeSubmitText: this.hasStripeSubmitTextTarget,
      stripeSpinner: this.hasStripeSpinnerTarget,
    }
    payLog("runCheckoutSetup: Stimulus targets", targetsOk)

    if (!this.hasStripeSubmitTarget || !this.hasPaymentElementMountTarget || !mountEl) {
      payError("runCheckoutSetup: aborting — missing required targets or mount element", {})
      return
    }

    payLog("runCheckoutSetup: mount node geometry", {
      tag: mountEl.tagName,
      width: mountEl.offsetWidth,
      height: mountEl.offsetHeight,
      display: window.getComputedStyle(mountEl).display,
      visibility: window.getComputedStyle(mountEl).visibility,
    })

    this.stripeSubmitTarget.disabled = true
    if (this.hasStripeErrorElementTarget) {
      this.stripeErrorElementTarget.textContent = ""
    }

    try {
      await loadStripeJs()
      if (mountEl === this.paymentElementMountTarget) {
        await waitForModalLayout()
      }

      const rawPurchase = this.data.get("stripe-purchase-data")
      if (!rawPurchase) {
        throw new Error("Missing purchase data for payment.")
      }
      let base
      try {
        base = JSON.parse(rawPurchase)
      } catch (parseErr) {
        payError("JSON.parse(stripe-purchase-data) failed", { rawPurchase, parseErr })
        throw parseErr
      }
      const cached = this._piCache[rail]
      let piId, clientSecret

      if (cached) {
        payLog("runCheckoutSetup: reusing cached PI for rail", { rail, piId: cached.id })
        piId = cached.id
        clientSecret = cached.clientSecret
      } else {
        const signUpFields = this.extractSignUpFields()
        const body = Object.assign({}, base, {
          payment_rail: rail,
          client_id: signUpFields.client_id,
          name: signUpFields.name,
          email: signUpFields.email,
        })

        const intentPath = this.data.get("create-stripe-payment-intent-path")
        payLog("POST create payment intent", { intentPath, body })

        const res = await fetch(intentPath, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
          },
          body: JSON.stringify(body),
        })

        if (!res.ok) {
          let errBody = ""
          try {
            errBody = await res.text()
          } catch (_e) {
            /* ignore */
          }
          payError("create payment intent HTTP error", {
            status: res.status,
            statusText: res.statusText,
            bodyPreview: errBody.slice(0, 500),
          })
          throw new Error("Could not start payment. Please try again.")
        }

        const data = await res.json()
        payLog("create payment intent OK", {
          payment_intent_id: data.payment_intent_id,
          client_secret_prefix: data.payment_intent_client_secret
            ? String(data.payment_intent_client_secret).slice(0, 12) + "…"
            : "(missing)",
        })
        piId = data.payment_intent_id
        clientSecret = data.payment_intent_client_secret
        this._piCache[rail] = { id: piId, clientSecret }
      }

      this._clientSecret = clientSecret

      const piField = document.getElementById("paymentIntentId")
      if (piField) {
        piField.value = piId
      }

      const publishableKey = this.data.get("stripe-publishable-key")
      payLog("publishable key", summarizePublishableKey(publishableKey))
      if (!publishableKey) {
        throw new Error("Stripe publishable key is missing. Check credentials for this environment.")
      }

      this.stripe = window.Stripe(publishableKey)
      this.elements = this.stripe.elements({
        clientSecret: this._clientSecret,
        appearance: { theme: "stripe" },
      })

      this.paymentElement = this.elements.create("payment", {
        layout: "tabs",
        wallets: { link: "never" },
        fields: {
          billingDetails: { name: "never", email: "never" },
        },
      })
      this.paymentElement.mount(mountEl)
      payLog("paymentElement.mount() called", { preload: mountEl !== this.paymentElementMountTarget })

      this.paymentElement.on("ready", () => {
        payLog("paymentElement: ready — enabling submit", {
          mountChildCount: mountEl.childNodes.length,
        })
        this.stripeSubmitTarget.disabled = false
      })

      this.paymentElement.on("change", (event) => {
        payLog("paymentElement: change", {
          complete: event.complete,
          empty: event.empty,
          valueType: event.value?.type,
        })
      })

      if (attachSubmitHandler) {
        this.ensureSubmitHandler()
      }
      payLog("runCheckoutSetup: done")
    } catch (e) {
      payError("runCheckoutSetup failed", e)
      if (this.hasStripeErrorElementTarget && mountEl === this.paymentElementMountTarget) {
        this.stripeErrorElementTarget.textContent = e.message || "Payment setup failed."
      }
      if (this.hasStripeSubmitTarget) {
        this.stripeSubmitTarget.disabled = false
      }
      throw e
    }
  }

  async confirmWithStripe() {
    if (!this.stripe || !this.elements || !this._clientSecret) {
      return
    }
    this.loading(true)
    if (this.hasStripeErrorElementTarget) {
      this.stripeErrorElementTarget.textContent = ""
    }

    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    const signUpData = document.querySelector('#paymentForm [name="sign_up[data]"]')?.value
    const paymentMethod = document.querySelector(
      '#paymentForm [name="sign_up[payment_method]"]:checked',
    )?.value

    sessionStorage.setItem(
      "accountsSignUpStripeResume",
      JSON.stringify({
        placeOrderUrl: this.data.get("place-order-url"),
        csrfToken: csrf,
        signUpData: signUpData,
        paymentMethod: paymentMethod,
      }),
    )

    const signUpFields = this.extractSignUpFields()
    const { error, paymentIntent } = await this.stripe.confirmPayment({
      elements: this.elements,
      confirmParams: {
        return_url: this.data.get("stripe-return-url"),
        payment_method_data: {
          billing_details: {
            name: signUpFields.name,
            email: signUpFields.email,
          },
        },
      },
      redirect: "if_required",
    })

    this.loading(false)

    if (error) {
      this.showError(error.message)
      return
    }

    if (
      paymentIntent &&
      (paymentIntent.status === "succeeded" || paymentIntent.status === "processing")
    ) {
      this.completeOrder(paymentIntent.id)
    }
  }

  showError(errorMsgText) {
    this.loading(false)
    const errorMsg = this.stripeErrorElementTarget
    const fixed = errorMsgText.replace(/postal/i, "zip code")
    errorMsg.textContent = fixed
    setTimeout(function () {
      errorMsg.textContent = ""
    }, 12000)
  }

  completeOrder(paymentIntentId) {
    this.loading(false)
    this.stripeSubmitTarget.disabled = true
    this._piCache = {}
    const piField = document.getElementById("paymentIntentId")
    if (piField) {
      piField.value = paymentIntentId
    }
    document.getElementById("paymentForm").submit()
  }

  loading(isLoading) {
    if (isLoading) {
      this.stripeSubmitTarget.disabled = true
      this.stripeSpinnerTarget.classList.remove("hidden")
      this.stripeSubmitTextTarget.classList.add("hidden")
    } else {
      this.stripeSubmitTarget.disabled = false
      this.stripeSpinnerTarget.classList.add("hidden")
      this.stripeSubmitTextTarget.classList.remove("hidden")
    }
  }

  extractSignUpFields() {
    const dataField = document.querySelector('#paymentForm [name="sign_up[data]"]')
    if (!dataField || !dataField.value) {
      payWarn("extractSignUpFields: sign_up[data] field missing or empty")
      return { client_id: null, name: null, email: null }
    }
    try {
      const data = JSON.parse(dataField.value)
      const nameParts = [data.first_name, data.last_name].filter(Boolean)
      return {
        client_id: data.client_id || null,
        name: nameParts.join(" ") || null,
        email: data.email || null,
      }
    } catch (e) {
      payWarn("extractSignUpFields: could not parse sign_up[data]", e)
      return { client_id: null, name: null, email: null }
    }
  }
}
